import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from './icons';

export function MultilevelListIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      {/* 1 */}
      <text x="1" y="6.5" fill="#0284c7" fontSize="6.5" fontWeight="700" fontFamily="system-ui, -apple-system, sans-serif">1</text>
      <line x1="6.5" y1="4.6" x2="18.5" y2="4.6" stroke="#334155" strokeWidth="1.7" strokeLinecap="round" />
      {/* a - indented */}
      <text x="3.5" y="12.2" fill="#0284c7" fontSize="6" fontWeight="700" fontFamily="system-ui, -apple-system, sans-serif">a</text>
      <line x1="8.5" y1="10" x2="18.5" y2="10" stroke="#334155" strokeWidth="1.7" strokeLinecap="round" />
      {/* i - indented further */}
      <text x="6" y="17.5" fill="#0284c7" fontSize="5.5" fontWeight="700" fontFamily="system-ui, -apple-system, sans-serif">i</text>
      <line x1="10" y1="15.4" x2="18.5" y2="15.4" stroke="#334155" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

const MULTILEVEL_STYLES = [
  {
    id: 'standard-outline',
    label: 'Standard Outline (1. / a. / i.)',
    samples: ['1. Heading 1', '   a. Sub-item A', '      i. Detail item'],
  },
  {
    id: 'legal-outline',
    label: 'Numeric Outline (1. / 1.1 / 1.1.1)',
    samples: ['1. Section 1', '   1.1 Sub-clause', '      1.1.1 Paragraph'],
  },
  {
    id: 'roman-outline',
    label: 'Formal Outline (I. / A. / 1.)',
    samples: ['I. Article One', '   A. Clause Alpha', '      1. Item One'],
  },
];

export function MultilevelListSelector({ editor }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!editor) return null;

  const isOrderedActive = editor.isActive('orderedList');

  const handleToggleDefault = (e) => {
    e.preventDefault();
    if (isOrderedActive) {
      editor.chain().focus().toggleOrderedList().run();
    } else {
      editor
        .chain()
        .focus()
        .toggleOrderedList()
        .updateAttributes('orderedList', { listStyleType: 'decimal' })
        .run();
    }
  };

  const handleSelectStyle = (styleId) => {
    if (!isOrderedActive) {
      editor
        .chain()
        .focus()
        .toggleOrderedList()
        .updateAttributes('orderedList', { listStyleType: 'decimal' })
        .run();
    }
    setIsOpen(false);
  };

  return (
    <div
      className="multilevel-list-selector-container"
      ref={containerRef}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
    >
      <div
        className={`toolbar-btn-group ${isOrderedActive ? 'is-active' : ''}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          borderRadius: '6px',
          border: '1px solid transparent',
          backgroundColor: isOrderedActive ? '#eff6ff' : 'transparent',
        }}
      >
        <button
          type="button"
          className={`toolbar-btn ${isOrderedActive ? 'is-active' : ''}`}
          onClick={handleToggleDefault}
          title="Toggle Multilevel List (1. / a. / i.)"
          style={{
            padding: '4px 6px',
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '32px',
          }}
        >
          <MultilevelListIcon size={18} />
        </button>

        <button
          type="button"
          className={`toolbar-btn ${isOpen ? 'is-active' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          title="Multilevel List Options (1. a. i., 1. 1.1, I. A. 1.)"
          style={{
            padding: '4px 3px',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderLeft: '1px solid rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '32px',
          }}
        >
          <ChevronDown size={12} style={{ opacity: 0.7 }} />
        </button>
      </div>

      {isOpen && (
        <div
          className="list-style-popover"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '6px',
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            zIndex: 1000,
            width: '280px',
            maxHeight: '400px',
            overflowY: 'auto',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Multilevel List Library
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '4px' }}>
            {MULTILEVEL_STYLES.map((style, idx) => {
              const isSelected = isOrderedActive && idx === 0;
              return (
                <button
                  key={style.id}
                  type="button"
                  onClick={() => handleSelectStyle(style.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: isSelected ? '1.5px solid #0284c7' : '1px solid #f1f5f9',
                    backgroundColor: isSelected ? '#f0f9ff' : '#f8fafc',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = '#f1f5f9';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = '#f8fafc';
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: isSelected ? '#0369a1' : '#334155' }}>
                      {style.label}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace', lineHeight: 1.3 }}>
                      <div>{style.samples[0]}</div>
                      <div>{style.samples[1]}</div>
                      <div>{style.samples[2]}</div>
                    </div>
                  </div>
                  {isSelected && <Check size={16} color="#0284c7" style={{ flexShrink: 0 }} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default MultilevelListSelector;
